import api from '@/client/core/api'

import { InstagramInstantwinMessageSendCountsResponse, MessageSendCountSortBy } from '../types'

export const MESSAGE_SEND_COUNT_SORT_KEYS = ['messageType', 'receivedCount'] as const

export type GetInstagramInstantwinMessageSendCountsParams = {
  campaignId: number
  prizeId: number
  startDatetime: string
  endDatetime: string
  sortBy?: MessageSendCountSortBy | string
  pagingNo?: number
  limitCount?: number
  forCsv?: boolean
}

export type InstagramInstantwinMessageSendCountsApiResponse = {
  data?: InstagramInstantwinMessageSendCountsResponse
  error?: {
    type?: string
    message?: string
    status?: number
  }
}

export class InstagramInstantwinMessageSendCountsApiError extends Error {
  constructor(
    public type: string,
    message?: string,
    public status?: number
  ) {
    super(message || type)
  }
}

export const DEFAULT_PAGING_NO = 1
export const DEFAULT_LIMIT_COUNT = 25
export const MAX_LIMIT_COUNT = 100

const SORT_KEY_SET = new Set<string>(MESSAGE_SEND_COUNT_SORT_KEYS)

const normalizeSortBy = (raw?: MessageSendCountSortBy | string): MessageSendCountSortBy | undefined => {
  if (!raw) return undefined
  const trimmed = raw.toString().trim()
  if (!trimmed) return undefined
  const key = trimmed.startsWith('-') ? trimmed.slice(1) : trimmed
  if (!SORT_KEY_SET.has(key)) return undefined
  return trimmed as MessageSendCountSortBy
}

const normalizePagingNo = (value?: number): number => {
  if (!Number.isFinite(value)) return DEFAULT_PAGING_NO
  return Math.max(DEFAULT_PAGING_NO, Number(value))
}

const normalizeLimitCount = (value?: number): number => {
  if (!Number.isFinite(value)) return DEFAULT_LIMIT_COUNT
  const normalized = Math.max(1, Number(value))
  return Math.min(normalized, MAX_LIMIT_COUNT)
}

export async function getInstagramInstantwinMessageSendCounts(
  params: GetInstagramInstantwinMessageSendCountsParams
): Promise<InstagramInstantwinMessageSendCountsResponse> {
  const forCsv = Boolean(params.forCsv)
  const sortBy = normalizeSortBy(params.sortBy)

  const requestParams: Record<string, unknown> = {
    prizeId: params.prizeId,
    startDatetime: params.startDatetime,
    endDatetime: params.endDatetime,
    forCsv
  }

  if (sortBy) {
    requestParams.sortBy = sortBy
  }

  if (!forCsv) {
    requestParams.pagingNo = normalizePagingNo(params.pagingNo)
    requestParams.limitCount = normalizeLimitCount(params.limitCount)
  }

  const response = await api.get<InstagramInstantwinMessageSendCountsApiResponse>(
    `/instagram_instantwin_campaign_analytics/${params.campaignId}/message_send_counts`,
    {
      params: requestParams
    }
  )

  if (response?.error?.type) {
    throw new InstagramInstantwinMessageSendCountsApiError(
      response.error.type,
      response.error.message,
      response.error.status
    )
  }

  return (
    response.data ?? {
      rows: [],
      dataCount: 0
    }
  )
}
